import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { getUsers } from '../api/generated/arrayscapeGamingDashboardAPI';
import type { User } from '../api/generated/model';

// useAllUsers fetches the complete user list, walking the cursor
// pagination until exhausted. Use this whenever the call site treats
// the response as an exhaustive list (user_id → name lookup maps,
// "all users" pickers, attribution validation against every user).
//
// `useGetUsers` from the generated client returns at most one page
// (default 20, spec-capped at 100). Treating that single page as
// "all users" silently misses anyone past the page boundary — a
// production incident already surfaced this when a user_id from a
// linked Payee resolved to null because they were on page 2.
//
// React-query keys this against a constant ['/api/v1/users','all',…]
// so it caches independently of the paginated useGetUsers calls. The
// cost is one extra round trip per 100 users; for our scale this is
// trivial.
export function useAllUsers(options?: { enabled?: boolean }): UseQueryResult<User[], Error> {
  return useQuery<User[], Error>({
    enabled: options?.enabled ?? true,
    queryKey: ['/api/v1/users', 'all', { sort_field: 'first_name', sort_direction: 'asc' }],
    queryFn: async () => {
      const collected: User[] = [];
      let resp: any = await getUsers({ sort_field: 'first_name', sort_direction: 'asc' });
      collected.push(...((resp?.data) || []));
      while (resp?.pagination?.cursor) {
        resp = await getUsers({ cursor: resp.pagination.cursor });
        collected.push(...((resp?.data) || []));
      }
      return collected;
    },
  });
}
